/**
 * Internal messaging (chat-style threads) — used by parents, teachers, admins.
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Envelope, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ to_user_id: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/messages").then((r) => setRows(r.data || []));
  useEffect(() => {
    refresh().catch(() => {});
    // Parents get a curated list of teachers/admins; others see no built-in contacts.
    if (user?.role === "parent") {
      api.get("/parent/contacts").then((r) => setContacts(r.data || []));
    }
  }, [user]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.to_user_id || !form.body.trim()) { toast.error("Pick recipient + write a message"); return; }
    setBusy(true);
    try {
      await api.post("/messages", form);
      setForm({ to_user_id: form.to_user_id, subject: "", body: "" });
      refresh();
      toast.success("Sent");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const markRead = async (id) => {
    try { await api.post(`/messages/${id}/read`); refresh(); }
    catch (err) {
      // Non-fatal — receipt marking is best-effort; surface to console only.
      logger.warn("Failed to mark message read", id, err?.response?.data?.detail || err.message);
    }
  };

  return (
    <DashboardLayout title="Messages" subtitle="Internal mail between parents, teachers and admins.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={send} className="vs-card p-5 space-y-3" data-testid="msg-form">
          <div className="eyebrow flex items-center gap-1.5"><PaperPlaneTilt size={12} /> New message</div>
          {user?.role === "parent" && contacts.length > 0 ? (
            <div>
              <Label>To</Label>
              <Select value={form.to_user_id} onValueChange={(v) => setForm({ ...form, to_user_id: v })}>
                <SelectTrigger data-testid="msg-to" className="bg-white"><SelectValue placeholder="Pick teacher / admin" /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — {c.role}{c.subject ? ` (${c.subject})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div><Label>Recipient user ID</Label><Input data-testid="msg-to" value={form.to_user_id} onChange={(e) => setForm({ ...form, to_user_id: e.target.value })} className="bg-white font-mono" placeholder="user_id from /admin/users" /></div>
          )}
          <div><Label>Subject (optional)</Label><Input data-testid="msg-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white" /></div>
          <div><Label>Message *</Label><Textarea data-testid="msg-body" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="bg-white" /></div>
          <Button type="submit" disabled={busy} data-testid="msg-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            <PaperPlaneTilt size={12} className="mr-1.5" /> {busy ? "Sending…" : "Send"}
          </Button>
        </form>

        <div className="lg:col-span-2 vs-card p-5 max-h-[600px] overflow-y-auto" data-testid="msg-list">
          <div className="eyebrow mb-3">Inbox & sent</div>
          <ul className="space-y-2">
            {rows.map((m) => {
              const inbound = m.to_user_id === user?.id;
              return (
                <li key={m.id} className={`p-3 rounded-md border ${m.read_at ? "border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]" : "border-[var(--vs-secondary)]/40 bg-[var(--vs-secondary)]/5"}`} data-testid={`msg-row-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs">
                      <Badge variant="outline" className="text-[10px] mr-2">{inbound ? "INBOUND" : "SENT"}</Badge>
                      <span className="font-mono">{inbound ? `from ${m.from_user_name}` : `to ${m.to_user_id.slice(0, 8)}…`}</span>
                      <span className="text-[var(--vs-text-secondary)] ml-2">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    {inbound && !m.read_at && (
                      <Button size="sm" variant="ghost" onClick={() => markRead(m.id)} data-testid={`msg-read-${m.id}`}><Envelope size={12} /> mark read</Button>
                    )}
                  </div>
                  {m.subject && <div className="text-sm font-semibold mt-1">{m.subject}</div>}
                  <div className="text-sm text-[var(--vs-text-secondary)] mt-1 whitespace-pre-wrap">{m.body}</div>
                </li>
              );
            })}
            {rows.length === 0 && <li className="text-sm text-[var(--vs-text-secondary)] text-center py-10">No messages yet.</li>}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
